import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CwIcon } from '@bbraun/cortex-angular';
import { filter, map } from 'rxjs';

import { localeFromUrl, stripLocalePrefix } from '../../../i18n/locales';
import { TranslationService } from '../../../shell/translation-service';
import { MainNavigationLock } from '../main-navigation-lock';
import { findNavItem, FOOTER_NAV_ITEMS, PRIMARY_NAV_ITEMS } from '../nav-items';
import { SidebarNavigationState } from '../sidebar-navigation-state';

/**
Custom-built navigation — no cortex-web CwNavigationDrawer/
CwNavigationDrawerSection/CwNavigationDrawerItem. Those Stencil
components render their own internal markup unconditionally (e.g. an
auto-injected divider between sections with no way to remove it via
inputs) and expose no CSS override surface (no `part` attributes), so
anything beyond their built-in look needed a shadow-root reach-in. Plain
semantic markup here avoids that entirely — no shadow DOM to fight.

Collapse behavior: SidebarNavigationState.visible is the single shared
toggle (flipped by MainFooter's collapse button). MainNav owns its own
presentation entirely — its :host width/visibility drive app-root's grid
column (an `auto`-sized track), so app-root itself carries no collapse
logic at all. By default (`collapseTextOnly` unset/false) toggling the
shared signal off hides this component entirely (width 0, visibility
hidden). Passing `[collapseTextOnly]="true"` opts a particular usage into
a different presentation instead: MainNav stays visible at a narrow,
icon-only width and hides just its text labels (still present in the DOM,
only visually collapsed, so screen readers still get them) — see
main-nav.scss's `.collapsed`/`.hidden` host rules.
*/
@Component({
  selector: 'ima-main-nav',
  imports: [CwIcon, RouterLink],
  templateUrl: './main-nav.html',
  styleUrl: './main-nav.scss',
  host: {
    '[class.collapsed]': 'isTextCollapsed()',
    '[class.hidden]': 'isFullyHidden()',
  },
})
export class MainNav {
  readonly collapseTextOnly = input(false);
  protected readonly navigationLocked = inject(MainNavigationLock).locked;

  protected readonly i18n = inject(TranslationService);

  protected readonly footerNavItems = computed(() =>
    FOOTER_NAV_ITEMS.map((item) => ({ ...item, label: this.i18n.t(item.labelKey) })),
  );

  private readonly sidebarState = inject(SidebarNavigationState);

  protected readonly isFullyHidden = computed(
    () => !this.collapseTextOnly() && !this.sidebarState.visible(),
  );

  protected readonly isTextCollapsed = computed(
    () => this.collapseTextOnly() && !this.sidebarState.visible(),
  );

  protected readonly primaryNavItems = computed(() =>
    PRIMARY_NAV_ITEMS.map((item) => ({ ...item, label: this.i18n.t(item.labelKey) })),
  );

  private readonly router = inject(Router);

  // standalone.routes.ts locale-prefixes every path (e.g. "/en/dashboard")
  // for both the dev server and the build-standalone production build;
  // app.routes.ts (the federation entry) never does. Deriving the prefix
  // from the current URL lets this one template work under both bootstraps
  // without either needing to tell MainNav which one is active.
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly localePrefix = computed(() => {
    return stripLocalePrefix(this.currentUrl()) === this.currentUrl()
      ? ''
      : `/${localeFromUrl(this.currentUrl())}`;
  });

  protected isActive(route: string): boolean {
    return findNavItem(this.currentUrl())?.route === route;
  }

  protected resolveRoute(route: string): string {
    return `${this.localePrefix()}${route}`;
  }
}
